/**
 * Project lifecycle (docs/specs/cloud/project-lifecycle.md; MB decisions 416, 417, 418, 423, 426).
 *
 * The rules are here only. The database mirrors them in `supabase/migrations/20261021030100_project_lifecycle.sql`
 * (`project_status_setters`, `field_crew_sees_status`, `project_access_for`, `project_field_data_tables`) and
 * `supabase/tests/rls-lifecycle.test.ts` checks every role, status and transition against this file. Roles are
 * additive: a person may do what any of their roles allows.
 */
import { planProgress } from './progress.js';
import type { LineStatus, PlannedLine } from './model.js';

export const PROJECT_STATUSES = ['planned', 'mobilising', 'active', 'complete', 'archived'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABEL: Readonly<Record<ProjectStatus, string>> = {
  planned: 'Planned',
  mobilising: 'Mobilising',
  active: 'Active',
  complete: 'Complete',
  archived: 'Archived',
};

export function isProjectStatus(v: unknown): v is ProjectStatus {
  return typeof v === 'string' && (PROJECT_STATUSES as readonly string[]).includes(v);
}

/** "Mobilising"; an unknown value is shown as written. */
export const projectStatusLabel = (s: string): string => (isProjectStatus(s) ? PROJECT_STATUS_LABEL[s] : s);

/**
 * Who may set each status (decision 416; 423 for Mobilising). Planned is where every project starts; managers may
 * move a project back to it (spec §10 question 1).
 */
const SETTERS: Readonly<Record<ProjectStatus, readonly string[]>> = {
  planned: ['manager'],
  mobilising: ['manager'],
  active: ['tenant_admin', 'manager', 'office_staff'],
  complete: ['manager'],
  archived: ['tenant_admin', 'manager'],
};

/** Who may take a project out of Archived ("Restore", decision 416). */
const RESTORERS: readonly string[] = ['tenant_admin', 'manager'];

/** The roles that may set `status` (help text, tests, the SQL mirror). */
export const statusSetters = (status: ProjectStatus): readonly string[] => SETTERS[status];

/** May these roles move a project from `from` to `to`? */
export function canSetStatus(roles: readonly string[], from: ProjectStatus, to: ProjectStatus): boolean {
  if (from === to) return false;
  const any = (allowed: readonly string[]): boolean => roles.some((r) => allowed.includes(r));
  if (from === 'archived') return any(RESTORERS);
  return any(SETTERS[to]);
}

/** The statuses these roles may choose for a project now at `from`, in lifecycle order. */
export function statusChoices(roles: readonly string[], from: ProjectStatus): ProjectStatus[] {
  return PROJECT_STATUSES.filter((s) => canSetStatus(roles, from, s));
}

/** Where "Restore" returns an archived project: the status it had before it was archived. */
export function restoreStatus(archivedFrom: string | null): ProjectStatus {
  return isProjectStatus(archivedFrom) && archivedFrom !== 'archived' ? archivedFrom : 'active';
}

// ---------------------------------------------------------------- visibility

/**
 * The statuses field crew do not see (decision 416: Planned is not visible to field staff; decision 426, Q369:
 * field crew see a project, and it syncs to laptops, from Mobilising onwards). The one switch: to move the boundary,
 * change this list and `public.field_crew_sees_status()` (the SQL mirror), then run `public.project_access_rebuild()`.
 */
export const FIELD_CREW_HIDDEN_STATUSES: readonly ProjectStatus[] = ['planned'];

export const fieldCrewSeesStatus = (s: ProjectStatus): boolean => !FIELD_CREW_HIDDEN_STATUSES.includes(s);

/** Roles that see every status (decision 416). */
const SEE_ALL: readonly string[] = ['tenant_admin', 'manager', 'office_staff', 'data_processor'];

/**
 * A member's access to one project:
 * - `sync`: the project and its data, written as each table allows, streamed to laptops;
 * - `read`: read only, not streamed to laptops but for the project row (archived);
 * - `listed`: the project row only, so a laptop learns it is in the Deleted bin;
 * - `pilot`: a pilot-only sign-in (R156, decision 917), on a project field crew see that is neither archived nor in
 *   the bin: the project row, and on the phone only what the pilot policies give (blocks, lines, line status, hazard
 *   pins and the hazard pack, the aircraft's positions, the last days' flights for a receipt; adding hazard pins and
 *   fuel receipt and MR pictures). Never streamed to a laptop (the Sync Streams ask for `sync`). Any staff role wins.
 * - null: nothing.
 * Client users are not members: their access is the client portal's (not archived, not in the bin).
 */
export type ProjectAccess = 'sync' | 'read' | 'listed' | 'pilot';

export function projectAccess(
  roles: readonly string[],
  status: ProjectStatus,
  inBin: boolean,
): ProjectAccess | null {
  const sees =
    roles.some((r) => SEE_ALL.includes(r)) || (roles.includes('field_crew') && fieldCrewSeesStatus(status));
  if (!sees) {
    const pilot = roles.includes('pilot') && fieldCrewSeesStatus(status) && !inBin && status !== 'archived';
    return pilot ? 'pilot' : null;
  }
  if (inBin) return 'listed';
  return status === 'archived' ? 'read' : 'sync';
}

/** The client portal shows a project that is neither archived nor in the bin (decision 418). */
export const clientSeesProject = (p: {
  readonly status: string;
  readonly deleted_at: string | null;
}): boolean => p.status !== 'archived' && p.deleted_at === null;

// ---------------------------------------------------------------- Complete

/** Decision 416: suggest Complete when there is a plan and every original planned line is accepted in full. */
export function suggestComplete(statuses: readonly LineStatus[], plan: readonly PlannedLine[]): boolean {
  const { lines } = planProgress(
    statuses.filter((s) => s.deleted_at === null),
    plan,
  );
  return lines.length > 0 && lines.every((l) => l.state === 'accepted');
}

// ---------------------------------------------------------------- deleting (decision 417)

/** Tenant admins only. */
export const canDeleteProject = (roles: readonly string[]): boolean => roles.includes('tenant_admin');

/** The typed confirmation: the project number as written, ignoring case and spaces around it. */
export function deleteConfirmMatches(typed: string, number: string): boolean {
  const t = typed.trim();
  return t.length > 0 && t.toLowerCase() === number.trim().toLowerCase();
}

/** Days a project with field data stays in the Deleted bin before it is removed for good. */
export const BIN_DAYS = 30;

/** When a project put in the bin at `deletedAt` is removed for good (ISO). */
export function purgeAfter(deletedAt: string): string {
  return new Date(Date.parse(deletedAt) + BIN_DAYS * 86_400_000).toISOString();
}

/**
 * Tables whose rows make a project's deletion go to the bin instead of happening at once: anything recorded in the
 * field or filed, live or soft-deleted. Planning alone (blocks, planned lines, positions, flight plans) is not field
 * data. Mirrored by `public.project_field_data_tables()`.
 */
export const FIELD_DATA_TABLES = [
  'flight',
  'line_status',
  'daily_entry',
  'cal_result',
  'fuel_ledger_entry',
  'maintenance_item',
  'report',
  'qc_run',
  'archive_file',
  'project_file',
  // project photos (decisions 427-431)
  'project_photo',
] as const;

// ---------------------------------------------------------------- what Teslyn Field says

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "21 Oct 2026" in the laptop's own time zone. */
export function lifecycleDateText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const KEEP_FOLDER =
  'The project folder and its files stay on this laptop. Delete the folder yourself when you no longer need it.';

export type CloudProjectState =
  | { readonly kind: 'live' }
  | { readonly kind: 'archived' | 'deleted'; readonly title: string; readonly text: string };

/**
 * What the open project is in Teslyn Cloud, from its cloud row (the `tenant_projects` stream): live (sync as
 * usual), archived or in the Deleted bin (sync stops; the laptop's folder is never touched).
 */
export function cloudProjectState(row: {
  readonly status: string;
  readonly deleted_at: string | null;
}): CloudProjectState {
  if (row.deleted_at) {
    return {
      kind: 'deleted',
      title: `Deleted in Teslyn Cloud on ${lifecycleDateText(row.deleted_at)}`,
      text:
        `A tenant admin can restore it until ${lifecycleDateText(purgeAfter(row.deleted_at))}; after that it is ` +
        `removed from Teslyn Cloud. Nothing on this laptop is sent or received meanwhile, and the project is read ` +
        `only here. ${KEEP_FOLDER}`,
    };
  }
  if (row.status === 'archived') {
    return {
      kind: 'archived',
      title: 'Archived in Teslyn Cloud: read only',
      text: `Nothing on this laptop is sent or received until a manager restores it. ${KEEP_FOLDER}`,
    };
  }
  return { kind: 'live' };
}
